<template>
  <Component :is="viewLayout">
    <template #page-heading>
      <h1 class="heading heading--large">Análise das conversas</h1>
    </template>

    <template #page-filter>
      <CommonPageFilter :filters="['dateRange', 'action', 'csat', 'feeling', 'status']" />
    </template>

    <template #main>
      <div class="conversations-insight">
        <BaseGrid columns="2">
          <WidgetTop />
          <WidgetCsatReview />
          <WidgetTopTerms class="grid-span-2" />
          <WidgetConversationFeelings />
          <WidgetConversationAverage />
        </BaseGrid>

        <BasePaneTray
          class="smart-summary-tray"
          context="smartSummaryContext"
          :isOpen="isOpen"
        >
          <template #header>
            <div class="smart-summary-tray__header">
              <SvgIconSparkles />

              <h2>Sumário inteligente</h2>
            </div>

            <div class="smart-summary-tray__tags-container">
              <div
                v-for="tag in getFilterTags"
                :key="tag.label"
                class="smart-summary-tray__tag"
              >
                {{ tag.label }}
              </div>
            </div>
          </template>

          <template #body>
            <div class="smart-summary-tray__body">
              <BaseChatTypingIndicator :incomingMessage="getSmartSummary" />
            </div>
          </template>

          <template #footer>
            <div class="smart-summary-tray__footer">
              <button
                v-tooltip="{
                  theme: 'tooltip-top-left',
                  content: 'Esta resposta<br>foi útil'
                }"
                class="smart-summary-tray__like-button"
                :class="{ 'smart-summary-tray__like-button--active': isSmartSummaryLiked }"
                @click="setSmartSummaryLike({ like_state: 1 })"
              >
                <SvgIconLike />
              </button>

              <button
                v-tooltip="{
                  theme: 'tooltip-top-left',
                  content: 'Esta resposta<br>não foi útil'
                }"
                class="smart-summary-tray__like-button"
                :class="{ 'smart-summary-tray__like-button--active': isSmartSummaryDisliked }"
                @click="setSmartSummaryLike({ like_state: -1 })"
              >
                <SvgIconDislike />
              </button>
            </div>
          </template>
        </BasePaneTray>
      </div>
    </template>
  </Component>
</template>

<script>
import { layoutMixin } from '@/utils/mixins'
import BaseGrid from '@/components/base/BaseGrid'
import CommonPageFilter from '@/components/common/CommonPageFilter'
import WidgetAttendancesRefusal from '@/components/widget/WidgetAttendancesRefusal'
import WidgetCsatReview from '@/components/widget/WidgetCsatReview'
import WidgetTopTerms from '@/components/widget/WidgetTopTerms'
import WidgetTop from '@/components/widget/WidgetTop'
import WidgetConversationFeelings from '@/components/widget/WidgetConversationFeelings'
import WidgetConversationAverage from '@/components/widget/WidgetConversationAverage'
import BasePaneTray from '@/components/base/BasePaneTray'
import BaseChatTypingIndicator from '@/components/base/BaseChatTypingIndicator.vue'
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'ConversationsInsight',

  components: {
    CommonPageFilter,
    BaseGrid,
    WidgetAttendancesRefusal,
    WidgetCsatReview,
    WidgetTopTerms,
    WidgetTop,
    WidgetConversationFeelings,
    WidgetConversationAverage,
    BasePaneTray,
    BaseChatTypingIndicator,
  },

  mixins: [layoutMixin],

  computed: {
    ...mapGetters('smartSummary', [
      'getSmartSummary',
      'isSmartSummaryLiked',
      'isSmartSummaryDisliked',
      'getFilterTags',
    ]),
  },

  data() {
    return {
      isOpen: false,
    };
  },

  mounted() {
    this.$root.$on('pane-tray-close', this.closeSmartSummary);
    this.$root.$on('pane-tray-open', this.openSmartSummary);
  },

  methods: {
    ...mapActions('smartSummary', ['setSmartSummaryLike']),

    closeSmartSummary() {
      this.isOpen = false;
    },

    openSmartSummary() {
      this.isOpen = true;
    },
  },
};
</script>

<style lang="scss" scoped>
.conversations-insight {
}

.smart-summary-tray {
  $color-gray: #e4e3e3;
  $color-blue: nth($colors-custom, 1);
  $color-light-blue: nth($colors-custom, 2);
  $color-gradient-green: rgba(203, 255, 0, 0.7);
  $color-gradient-blue: rgba(152, 193, 221, 0.7);

  /deep/ {
    .pane-tray {
      &__container {
        width: 500px;
      }

      &__body {
        overflow: hidden;
      }

      &__button--close {
        top: 37px;
        svg {
          fill: $color-blue;
        }
      }
    }
  }

  &__tags-container {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-bottom: 20px;
    padding: 20px 20px 0 20px;
  }

  &__tag {
    display: inline-flex;
    height: 30px;
    align-items: center;
    padding: 0 10px;
    border-radius: 2em;
    background: rgba(nth($colors-custom, 2), 0.25);
    color: nth($colors-custom, 1);
    font: {
      weight: 500;
      size: 10px;
    }
    white-space: nowrap;
  }

  &__header {
    background: linear-gradient(0deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0.2) 100%),
      linear-gradient(94deg, $color-gradient-blue 2.73%, $color-gradient-green 103.24%);
    display: flex;
    align-items: center;
    padding: 36px;
    gap: 20px;

    svg {
      width: 30px;
      height: 30px;
      fill: $color-blue;
    }

    h2 {
      font-size: 20px;
      font-style: normal;
      font-weight: 700;
      line-height: 30px;
    }
  }

  &__body {
    @extend %scrollbar;
    overflow: auto;
    height: 100%;
    padding-right: 8px;
    overscroll-behavior: contain;
  }

  &__footer {
    padding: 36px 20px;
    display: flex;
    justify-content: flex-end;
    gap: 16px;
  }

  &__like-button {
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 0;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;

    &--active {
      background: $color-gray;
    }

    svg {
      width: 100%;
      height: 100%;
      fill: $color-blue;
    }

    &:hover {
      svg {
        fill: $color-light-blue;
      }
    }
  }
}
</style>
